// src/components/LoginForm.tsx
import React from 'react';
import Spinner from './Spinner';

interface LoginFormProps {
    handleLogin: () => Promise<boolean | void>;
    username: string;
    setUsername: (value: string) => void;
    showPassword: boolean;
    password: string;
    setPassword: (value: string) => void;
    setShowPassword: (value: boolean) => void;
    isPending?: boolean;
    error: {
        Username?: string;
        Password?: string;
        General?: string;
    };
    setError: (error: LoginFormProps['error']) => void;
    switchToForgotPassword: () => void;
}

function LoginForm(props: LoginFormProps) {
    const {
        handleLogin,
        username,
        setUsername,
        showPassword,
        password,
        setPassword,
        setShowPassword,
        isPending,
        error,
        setError,
        switchToForgotPassword,
    } = props;

    const [rememberMe, setRememberMe] = React.useState(() => {
        const saved = localStorage.getItem('rememberMe');
        return saved === 'true';
    });

    React.useEffect(() => {
        if (rememberMe) {
            const savedUsername = localStorage.getItem('savedUsername');
            const savedPassword = localStorage.getItem('savedPassword');
            if (savedUsername) setUsername(savedUsername);
            if (savedPassword) setPassword(savedPassword);
        }
    }, []);

    React.useEffect(() => {
        if (rememberMe) {
            localStorage.setItem('savedUsername', username);
            localStorage.setItem('savedPassword', password);
        }
    }, [username, password, rememberMe]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const clearUsername = () => {
        setUsername('');
        setError({ ...error, Username: '', General: '' });
    };

    function handleUsernameOnChange(value: string) {
        setError({ ...error, Username: '', General: '' });
        setUsername(value);
    }

    function handlePasswordOnChange(value: string) {
        setError({ ...error, Password: '', General: '' });
        setPassword(value);
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
            }}
            className="flex flex-col items-center w-full max-w-[494px] bg-white p-10 pt-14 rounded-[18px] shadow-lg"
        >
            <div className="w-full flex flex-col grow">
                {/* Logo & Branding */}
                <div className="flex flex-col items-center w-full">
                    <img
                        loading="lazy"
                        srcSet="/athlyte_logo.png"
                        className="w-[73px] h-[50px]"
                        alt="Athlyte logo"
                    />
                    <p className="mt-1 text-[16px] font-bold text-black">
                        Athlyte College Sports Communicator
                    </p>
                    <p className="mt-1 text-[14px] font-semibold text-black/30">
                        Lead the future of sports storytelling
                    </p>
                </div>

                {/* Welcome Text */}
                <div className="flex flex-col items-center w-full mt-10">
                    <p className="text-[20px] font-medium text-[#292929]">Welcome Back!</p>
                    <p className="mt-2 text-[13px] font-normal text-[#5B5B5B]">
                        Please enter your account details
                    </p>
                </div>

                {/* General Error */}
                {error.General && !error.Username && !error.Password && (
                    <p className="text-[#DF1E1E] text-sm text-center mb-3">{error.General}</p>
                )}

                {/* Username Field */}
                <div className="flex flex-col mt-5 w-full">
                    <label className="text-sm font-medium text-neutral-600 mb-1">Username</label>
                    <div className="relative">
                        <input
                            type="text"
                            id="username"
                            name="username"
                            autoComplete="username"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => handleUsernameOnChange(e.target.value)}
                            className={`w-full py-2 pl-3 pr-10 rounded placeholder:text-neutral-400 ${
                                error.Username
                                    ? 'border-[#DF1E1E] bg-[#FDF6F6] border'
                                    : 'focus:border-[#2BACC9] focus:outline-none border-0 focus:border bg-[#F6FCFD] focus:bg-[#F6FBFE]'
                            }`}
                        />
                        {username && (
                            <img
                                src="/clear_icon.png"
                                alt="Clear"
                                onClick={clearUsername}
                                className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                            />
                        )}
                    </div>
                    {error.Username && (
                        <p className="text-[#DF1E1E] text-sm font-medium mt-1">{error.Username}</p>
                    )}
                </div>

                {/* Password Field */}
                <div className="flex flex-col mt-4 w-full">
                    <label htmlFor="password" className="text-sm font-medium text-neutral-700 mb-1">
                        Password
                    </label>
                    <div className="relative">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            autoComplete="current-password"
                            placeholder="Enter your Password"
                            value={password}
                            onChange={(e) => handlePasswordOnChange(e.target.value)}
                            className={`w-full py-2 pl-3 pr-10 rounded ${
                                error.Password
                                    ? '!border-[#DF1E1E] !bg-[#FDF6F6] border'
                                    : '!bg-[#F6FCFD] focus:bg-[#F6FBFE] focus:border-[#2BACC9] focus:outline-none border-0 focus:border'
                            }`}
                        />
                        <img
                            src={showPassword ? '/visible.svg' : '/invisible.svg'}
                            alt="Toggle visibility"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                            onClick={togglePasswordVisibility}
                        />
                    </div>
                    {error.Password && (
                        <p className="text-[#DF1E1E] text-sm font-medium mt-1">{error.Password}</p>
                    )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex gap-5 mt-3 w-full text-xs font-light justify-between items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                const checked = !rememberMe;
                                setRememberMe(checked);
                                if (!checked) {
                                    localStorage.removeItem('savedUsername');
                                    localStorage.removeItem('savedPassword');
                                    localStorage.removeItem('rememberMe');
                                } else {
                                    localStorage.setItem('rememberMe', 'true');
                                }
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                rememberMe ? 'bg-cyan-500' : 'bg-gray-300'
                            }`}
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                    rememberMe ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                            />
                        </button>
                        <span className="text-sm text-gray-600">Remember me</span>
                    </label>
                    <div
                        className="text-cyan-700 font-semibold hover:underline cursor-pointer"
                        onClick={(e) => {
                            e.preventDefault();
                            switchToForgotPassword();
                        }}
                    >
                        Forgot Password?
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col max-md:max-w-full mt-6 justify-center items-center">
                    <button
                        type="submit"
                        disabled={isPending}
                        className="flex flex-row justify-center items-center cursor-pointer self-stretch px-16 py-3 text-center text-lg text-white whitespace-nowrap bg-cyan-600 rounded disabled:opacity-60"
                    >
                        {isPending ? <Spinner /> : 'Login'}
                    </button>
                </div>

                {/* Footer Links */}
                <div className="mt-6 text-center text-sm">
                    <p className="mt-2">
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                window.location.href = '/info/index.html';
                            }}
                            className="text-cyan-700 font-semibold cursor-pointer hover:underline"
                        >
                            Help
                        </a>
                    </p>
                </div>
            </div>
        </form>
    );
}

export default LoginForm;